/* eslint-disable max-len */
/* eslint-disable no-use-before-define */
import moment from 'moment/moment';
import { storeLocalTime } from './store-time/store-time-utils';
import { militaryToStandardTime } from './mapper';
import {
  RESERVATION_BUFFERS,
  DEFAULT_STORE_HOURS,
  MILITARY_DATE_TIME,
  VEHICLE_CATEGORIES
} from '../constants/constants';
import { FAILED_AVAILABILTY } from '../constants/emptyAvailability';

const sortAvailability = ({ list, reservation }) => {
  return list.sort((store1, store2) => {
    if (store1?.availableHours?.length > 0 && !store2?.availableHours?.length > 0) {
      return -1;
    }
    if (
      (store1?.availableHours?.length > 0 === store2?.availableHours?.length > 0)
      && (
        helper.checkExactAvailability({ store: store1, reservation })
        && !helper.checkExactAvailability({ store: store2, reservation })
      )
    ) {
      return -1;
    }
    if (
      (store1?.availableHours?.length > 0 === store2?.availableHours?.length > 0)
      && (
        helper.checkExactAvailability({ store: store1, reservation })
        === helper.checkExactAvailability({ store: store2, reservation })
      )
    ) {
      return store1.distance - store2.distance;
    }
    return 1;
  });
};

const formatAvailability = ({ stores, availability }) => {
  return stores.reduce((arr, store) => {
    return [
      ...arr,
      {
        ...store,
        ...(availability?.find((availableStore) => availableStore?.locationNbr === store?.storeId) || FAILED_AVAILABILTY)
      }
    ];
  }, []);
};

const getHourFromString = (stringTime) => {
  return Number(stringTime.split(':')[0]);
};

const getRentalOrStoreHours = (store, action, date) => {
  if (action === 'pickup' && store?.rentalHours && store?.rentalHours?.startDateHours?.open !== '') {
    return store?.rentalHours?.startDateHours;
  }
  if (action === 'dropoff' && store?.rentalHours && store?.rentalHours?.endDateHours?.open !== '') {
    return store?.rentalHours?.endDateHours;
  }

  if (store?.storeHours) {
    return helper.getDatesHours(store?.storeHours, date);
  }

  return helper.getDatesHours(DEFAULT_STORE_HOURS, date);
};

const getDatesHours = (rentalStoreHours, reservationDate) => {
  const dayOfTheWeek = moment(reservationDate, 'MM/DD/YYYY').format('dddd').toLowerCase();
  return rentalStoreHours[dayOfTheWeek];
};

const getDatesClosingTime = (rentalStoreHours, reservationDate) => {
  const dayOfTheWeek = moment(reservationDate, 'MM/DD/YYYY').format('dddd').toLowerCase();
  return rentalStoreHours[dayOfTheWeek].close;
};

const getDatesOpeningTime = (rentalStoreHours, reservationDate) => {
  const dayOfTheWeek = moment(reservationDate, 'MM/DD/YYYY').format('dddd').toLowerCase();
  return rentalStoreHours[dayOfTheWeek].open;
};

const createTimeSlotsArray = (start, end) => {
  let data = [];
  let startingTime = moment(start, 'h:mm');
  let endTime = moment(end, 'h:mm');
  while (startingTime.isSameOrBefore(endTime)) {
    data.push({ value: startingTime.format('H:mm'), name: startingTime.format('h:mm a') });
    startingTime.add(15, 'minutes');
  }
  return data;
};

const getPickupTimeRange = ({
  storeNbr,
  rentalStoreHours,
  reservationDate
}) => {
  let tomorrow = new Date();
  tomorrow.setDate(new Date().getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);

  const currentTimeAtStore = storeLocalTime(storeNbr);

  const closingTime = helper.getHourFromString(helper.getDatesClosingTime(rentalStoreHours, reservationDate));
  const openingTime = helper.getHourFromString(helper.getDatesOpeningTime(rentalStoreHours, reservationDate));

  let isFutureReservation = (
    new Date(currentTimeAtStore) < new Date(reservationDate)
  );

  let isOpeningSlotAvailable = (
    new Date(currentTimeAtStore).getHours() < closingTime - RESERVATION_BUFFERS.openingSlotAvailable
  );

  let isReservationTomorrow = (new Date(reservationDate).getTime() === tomorrow.getTime());

  const lastResOpt = closingTime - RESERVATION_BUFFERS.beforeClose;
  const storeFirstResOpt = openingTime + RESERVATION_BUFFERS.afterOpen;

  let userFirstResOpt = currentTimeAtStore.getHours() + RESERVATION_BUFFERS.pickTime;

  const storeMinutes = currentTimeAtStore.getMinutes();

  if (storeMinutes >= 30) {
    userFirstResOpt += 1;
  }

  /*
  for a future date scenario, my available times should equal store open to 2 hrs prior to close

  for a same day scenario, my earliest available time should equal now (rounded) + 5hrs
  (without exceeding 2hrs prior to close)

  to make a reservation for tomorrow 4+ business hrs before closing all timeslots should be showing
  (ie 6am tomorrow morning pickup needs to be made by 5pm the night before if the store closes at 9)

  to make a reservation for tomorrow within 4 business hrs of closing timeslots should be 4 hours after opening
  (ie at 6pm first reservation option for tomorrow is 10am)
  */

  let firstResOpt;
  // (reservationDate === '11/18/2021') is just so we can test with static data
  if (isFutureReservation || reservationDate === '11/18/2021') {
    if (isReservationTomorrow && !isOpeningSlotAvailable) {
      firstResOpt = storeFirstResOpt;
    } else {
      firstResOpt = openingTime;
    }
  } else {
    firstResOpt = userFirstResOpt > storeFirstResOpt ? userFirstResOpt : storeFirstResOpt;
  }

  return {
    firstTime: `${firstResOpt}:00`,
    lastTime: `${lastResOpt}:00`
  };
};

const getReservationTimeOptions = ({
  storeNbr,
  rentalStoreHours,
  reservationDate,
}) => {
  const { firstTime, lastTime } = helper.getPickupTimeRange({
    storeNbr,
    rentalStoreHours,
    reservationDate
  });

  return helper.createTimeSlotsArray(firstTime, lastTime);
};

const getDeliveryTimeOptions = ({
  storeNbr,
  rentalStoreHours,
  deliveryDate
}) => {
  const { firstTime, lastTime } = helper.getPickupTimeRange({
    storeNbr,
    rentalStoreHours,
    reservationDate: deliveryDate
  });

  return helper.createTimeSlotsArray(firstTime, lastTime);
};

const getDropoffTimeRange = ({
  rentalStoreHours,
  reservationDate,
  pickupTime = ''
}) => {
  const [hour, minutes] = pickupTime.split(':');

  const closingTime = helper.getDatesClosingTime(rentalStoreHours, reservationDate);
  const openingTime = helper.getDatesOpeningTime(rentalStoreHours, reservationDate);

  const firstReturnTime = hour ? `${Number(hour) + RESERVATION_BUFFERS.minimunDuration}:${minutes}` : openingTime;

  return {
    firstTime: firstReturnTime,
    lastTime: closingTime
  };
};

const getEndingTimeOptions = ({
  rentalStoreHours,
  reservationDate,
  pickupTime = '',
}) => {
  const { firstTime, lastTime } = helper.getDropoffTimeRange({
    rentalStoreHours,
    reservationDate,
    pickupTime
  });

  return helper.createTimeSlotsArray(firstTime, lastTime);
};

const checkStoreValidDateTime = (reservationState, store, timeslot = 'pickup') => {
  if (timeslot !== 'pickup' && timeslot !== 'dropoff') {
    return {
      valid: false,
      errorMessage: 'BAD ACTION SENT!'
    };
  }

  let reservationTimeOption = new Date();
  let storeOpenHours = new Date();
  let storeCloseHours = new Date();
  let firstValidTime;
  let lastValidTime;

  const selectedDate = timeslot === 'pickup' ? reservationState?.reservation?.pickupDate : reservationState?.reservation?.dropoffDate;
  const selectedTime = timeslot === 'pickup' ? reservationState?.reservation?.pickupTime : reservationState?.reservation?.dropoffTime;

  const storeHours = helper.getRentalOrStoreHours(store, timeslot, selectedDate);

  const [selectedHours, selectedMinutes] = selectedTime.split(':');
  const [openHours, openMinutes] = storeHours?.open?.split(':');
  const [closeHours, closeMinutes] = storeHours?.close?.split(':');
  const closeHoursAfterBuffer = timeslot === 'pickup' ? Number(closeHours) - Number(RESERVATION_BUFFERS.beforeClose) : Number(closeHours);

  reservationTimeOption.setHours(Number(selectedHours), Number(selectedMinutes), 0, 0);
  storeOpenHours.setHours(Number(openHours), Number(openMinutes), 0, 0);
  storeCloseHours.setHours(closeHoursAfterBuffer, Number(closeMinutes), 0, 0);
  firstValidTime = `${openHours}:${openMinutes}`;
  lastValidTime = `${closeHoursAfterBuffer}:${closeMinutes}`;

  if (
    reservationTimeOption >= storeOpenHours
    && reservationTimeOption <= storeCloseHours
  ) {
    return { valid: true, errorMessage: '' };
  }

  if (timeslot === 'pickup') {
    if (reservationTimeOption < storeOpenHours) {
      return {
        valid: false,
        errorMessage: `Reservations for your selected store do not start until ${militaryToStandardTime(firstValidTime)}. Please update your pickup time to ${militaryToStandardTime(firstValidTime)} or after.`,
        validOption: firstValidTime
      };
    }
    return {
      valid: false,
      errorMessage: `Reservations for your selected store end at ${militaryToStandardTime(lastValidTime)}. Please update your pickup time to ${militaryToStandardTime(lastValidTime)} or before.`,
      validOption: lastValidTime
    };
  }

  if (reservationTimeOption < storeOpenHours) {
    return {
      valid: false,
      errorMessage: `Dropoff for your selected store does not start until ${militaryToStandardTime(firstValidTime)}. Please update your dropoff time to ${militaryToStandardTime(firstValidTime)} or after.`,
      validOption: firstValidTime
    };
  }
  return {
    valid: false,
    errorMessage: `Dropoff for your selected store ends at ${militaryToStandardTime(lastValidTime)}. Please update your dropoff time to ${militaryToStandardTime(lastValidTime)} or before.`,
    validOption: lastValidTime
  };
};

const triggerFormValidations = ({
  storeNbr,
  rentalStoreHours,
  reservation
}) => {
  let missingFields = [];
  const { pickupTime, pickupDate, dropoffTime, dropoffDate } = reservation;

  if (!pickupDate) {
    missingFields.push('pickupDate');
  }
  if (!pickupTime || !helper.validatePickupTime(reservation, storeNbr, rentalStoreHours)) {
    missingFields.push('pickupTime');
  }
  if (!dropoffDate || !helper.validateDropoffDate(reservation)) {
    missingFields.push('dropoffDate');
  }
  if (!dropoffTime || !helper.validateDropoffTime(reservation, storeNbr, rentalStoreHours)) {
    missingFields.push('dropoffTime');
  }

  return {
    missingFields,
    valid: (missingFields.length === 0)
  };
};

const validatePickupTime = (reservation, storeNbr, rentalStoreHours) => {
  const { pickupDate, pickupTime } = reservation;

  const { firstTime, lastTime } = getPickupTimeRange({ storeNbr, rentalStoreHours, reservationDate: pickupDate });

  let firstAllowed = moment(firstTime, 'H:mm');
  let lastAllowed = moment(lastTime, 'H:mm');
  let choice = moment(pickupTime, 'H:mm');

  return pickupTime && choice.isBetween(firstAllowed, lastAllowed, 'minute', '[]');
};

const validateDropoffDate = (reservation) => {
  const { pickupDate, pickupTime, dropoffDate } = reservation;

  let pickup = moment(pickupDate, 'MM/DD/YYYY');
  let dropoff = moment(dropoffDate, 'MM/DD/YYYY');

  return (
    pickupDate
    && pickupTime
    && dropoffDate
    && dropoff.isSameOrAfter(pickup, 'd')
  );
};

const validateDropoffTime = (reservation, storeNbr, rentalStoreHours) => {
  const { pickupDate, pickupTime, dropoffDate, dropoffTime } = reservation;

  const { firstTime, lastTime } = getDropoffTimeRange({ storeNbr, rentalStoreHours, reservationDate: dropoffDate });

  let firstAllowed = moment(firstTime, 'H:mm');
  let lastAllowed = moment(lastTime, 'H:mm');
  let choice = moment(dropoffTime, 'H:mm');

  let pickup = moment(`${pickupDate} ${pickupTime}`, MILITARY_DATE_TIME);
  let dropoff = moment(`${dropoffDate} ${dropoffTime}`, MILITARY_DATE_TIME);

  pickup.add(RESERVATION_BUFFERS.minimunDuration, 'hour');

  return (
    dropoffTime
    && pickup.isSameOrBefore(dropoff, 'minute')
    && choice.isBetween(firstAllowed, lastAllowed, 'minute', '[]')
  );
};

const validOptionSelection = (selected, options) => {
  return Boolean(options.find((option) => option.value === selected));
};

const scrollToSection = (identifier) => {
  const element = document.getElementById(identifier);
  const pos = element.style.position;
  const top = element.style.top;
  element.style.position = 'relative';
  element.style.top = '-63px';
  element.scrollIntoView({ behavior: 'smooth' });
  element.style.top = top;
  element.style.position = pos;

  return null;
};

const vehicleCheck = (rentalCategory) => {
  return VEHICLE_CATEGORIES.includes(rentalCategory);
};

const checkSelectedLocalizedStoreAvailability = ({ availabilityList, reservationState, localizedStore }) => {
  const localizedStoreAvailability = availabilityList.filter(
    (store) => store.storeId === (reservationState?.selectedStore?.storeId || localizedStore?.storeId?.padStart(4, '0'))
  );
  return Boolean(localizedStoreAvailability[0]?.availableHours?.length > 0);
};

const checkStoreAvailabilityStatus = ({ availabilityList, storeId, reservation }) => {
  const storeAvailability = availabilityList.filter(
    (store) => store.storeId === storeId.padStart(4, '0')
  );
  if (storeAvailability[0]?.availableHours?.length > 0) {
    if (
      helper.checkExactAvailability({
        store: storeAvailability[0],
        reservation
      })
    ) {
      return 'Exact';
    }
    return 'Close';
  }
  return 'Unavailable';
};

const checkAvailableForSelectedDate = ({ availabilityList }) => (availabilityList.length > 0 && (availabilityList.filter((str) => str.availableHours?.length > 0).length > 0));

const checkExactAvailability = ({ store, reservation }) => {
  const { availableHours } = store;
  const {
    pickupDate,
    pickupTime,
    dropoffDate,
    dropoffTime
  } = reservation;

  return availableHours?.some((slot) => {
    let reservationStart = moment(`${pickupDate} ${pickupTime}`, MILITARY_DATE_TIME);
    let reservationEnd = moment(`${dropoffDate} ${dropoffTime}`, MILITARY_DATE_TIME);

    return (
      moment(slot.start, MILITARY_DATE_TIME).isSame(reservationStart)
      && moment(slot.end, MILITARY_DATE_TIME).isSame(reservationEnd)
    );
  });
};

const mapTimeSlot = (store, reservationState, startDate, endDate) => {
  const { reservation: {
    pickupDate,
    pickupTime,
    dropoffDate,
    dropoffTime
  } } = reservationState;

  let start = '';
  let end = '';

  let pickupOption = moment(`${pickupDate} ${pickupTime}`, MILITARY_DATE_TIME);
  let dropoffOption = moment(`${dropoffDate} ${dropoffTime}`, MILITARY_DATE_TIME);

  if (pickupOption.isBetween(startDate, endDate, 'minute', '[]')) {
    const isPickupValidTime = helper.checkStoreValidDateTime(reservationState, store);
    if (isPickupValidTime.valid) {
      start = pickupOption;
    } else {
      let validPickupOption = moment(`${pickupDate} ${isPickupValidTime.validOption}`, MILITARY_DATE_TIME);
      start = validPickupOption;
    }
  } else {
    start = moment(startDate, 'YYYY-MM-DD H:mm');
  }

  if (dropoffOption.isBetween(startDate, endDate, 'minute', '[]')) {
    const isDropoffValidTime = helper.checkStoreValidDateTime(reservationState, store, 'dropoff');
    if (isDropoffValidTime.valid) {
      end = dropoffOption;
    } else {
      let validDropoffOption = moment(`${dropoffDate} ${isDropoffValidTime.validOption}`, MILITARY_DATE_TIME);
      end = validDropoffOption;
    }
  } else {
    end = moment(endDate, 'YYYY-MM-DD H:mm');
  }

  if (start.isSame(endDate, 'minute')) {
    start.subtract({ hours: RESERVATION_BUFFERS.minimunDuration });
  }

  if (end.isSame(startDate, 'minute')) {
    end.add({ hours: RESERVATION_BUFFERS.minimunDuration });
  }

  return {
    start: start.format(MILITARY_DATE_TIME),
    end: end.format(MILITARY_DATE_TIME)
  };
};

const remapAvailabilityData = (availabilityData, reservationState) => {
  return availabilityData.map((store) => {
    const newStore = { ...store };

    const { availableHours } = newStore;
    const newAvailableHours = [];

    if (availableHours) {
      availableHours.forEach((slot) => {
        let start = moment(slot.start, 'YYYY-MM-DD H:mm');
        let end = moment(slot.end, 'YYYY-MM-DD H:mm');

        if (end.diff(start, 'hours') >= RESERVATION_BUFFERS.minimunDuration) {
          newAvailableHours.push(
            helper.mapTimeSlot(store, reservationState, slot.start, slot.end)
          );
        }
      });

      // check if the selected reservation is an exact match and if so filter out the other options
      if (helper.checkExactAvailability({ store: { availableHours: newAvailableHours }, reservation: reservationState.reservation })) {
        newStore.availableHours = newAvailableHours.filter((slot) => (
          moment(slot.start, MILITARY_DATE_TIME).isSame(moment(reservationState.reservation.pickupDate + ' ' + reservationState.reservation.pickupTime, MILITARY_DATE_TIME))
          && moment(slot.end, MILITARY_DATE_TIME).isSame(moment(reservationState.reservation.dropoffDate + ' ' + reservationState.reservation.dropoffTime, MILITARY_DATE_TIME))
        ));
      } else {
        newStore.availableHours = newAvailableHours;
      }
    }

    return newStore;
  });
};

const graphQlHandler = async (graphQlTriggerCall, variables) => {
  const { data: dataRes, error: errorRes } = await graphQlTriggerCall({ variables });

  if (errorRes) {
    throw new Error(errorRes);
  }

  return dataRes;
};

const formatTimestampRangeWithMoment = (timestamp) => {
  // Parse the input timestamp with moment
  const startTime = moment(timestamp, 'YYYY-MM-DD HH:mm');

  // Add 2 hours to the start time to get the end time
  const endTime = startTime.clone().add(2, 'hours');

  // Format both start and end times
  const formattedStartTime = startTime.format('YYYY-MM-DD HH:mm');
  const formattedEndTime = endTime.format('HH:mm');

  // Combine into the desired format
  return `${formattedStartTime} - ${formattedEndTime}`;
};

const timeFormater = {
  '07:00': '7-9 AM',
  '08:00': '8-10 AM',
  '10:00': '10-12 PM',
  '11:00': '11-1 PM',
  '13:00': '1-3 PM',
  '15:00': '3-5 PM',
  '': 'Time',
};

const helper = {
  createTimeSlotsArray,
  sortAvailability,
  formatAvailability,
  getHourFromString,
  getDatesHours,
  getRentalOrStoreHours,
  getDatesClosingTime,
  getDatesOpeningTime,
  getPickupTimeRange,
  getReservationTimeOptions,
  getDeliveryTimeOptions,
  getDropoffTimeRange,
  getEndingTimeOptions,
  checkStoreValidDateTime,
  triggerFormValidations,
  validatePickupTime,
  validateDropoffDate,
  validateDropoffTime,
  validOptionSelection,
  scrollToSection,
  vehicleCheck,
  checkSelectedLocalizedStoreAvailability,
  checkStoreAvailabilityStatus,
  checkAvailableForSelectedDate,
  checkExactAvailability,
  mapTimeSlot,
  remapAvailabilityData,
  graphQlHandler,
  formatTimestampRangeWithMoment,
  timeFormater
};

export default helper;
